import { Injectable, inject } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { Data, NavigationEnd, Router } from "@angular/router";
import { AuthService } from "@core/auth.service";
import { getFunctions, httpsCallable } from "firebase/functions";
import { BehaviorSubject, from } from "rxjs";
import { startWith, take } from "rxjs/operators";
import { CommonVariable } from "src/app/app.constants";
import { PrizeBoxComponent } from "src/app/components/prize-box/prize-box.component";
import { PingService } from "src/app/services/ping.service";
import { RequestPaymentService } from "src/app/services/RequestBodyService/RequestPayment/request-payment.service";
import { GamesOnboardingService } from "../game-new/services/GamesOnboardingService";


@Injectable({
  providedIn: "root",
})
export class GamesRepositoryService {
  internetSpeed$ = new BehaviorSubject(null);
  public currencyCodeMapping = {
    "CAD": 1,
    "EUR": 353,
    "default": 44
  };

  currentGameOrientation$ = new BehaviorSubject<string>("");
  public gamesWithNoPrize = [216]; // to store game id's which don't have prize data: Fruit Loose, Enchanted Slots

  prizeUnlockID: any;
  private gamesOnboardingService = inject(GamesOnboardingService);

  constructor(
    private authService: AuthService,
    private pingService: PingService,
    private router: Router,
    private matDialog: MatDialog,
    private RequestPaymentService: RequestPaymentService,
  ) {
    this.router.events.pipe(startWith(this.router))?.subscribe((event: Data) => {
      if (event instanceof NavigationEnd && event?.url?.search("game") > -1) {
        this.pingService.pingNow()?.subscribe(
          (ping) => {
            if (ping < 600) {
              this.internetSpeed$.next("fast");
            } else {
              this.internetSpeed$.next("slow");
            }
          }
        );
      } else {
        this.internetSpeed$.next(null);
      }
    });
  }


  setCurrentGameOrientationStatus(status: string) {
    this.currentGameOrientation$.next(status);
  }


  //This service method is used to call the api for opening the prize box and then it will open the dialog.
  OpenPrizeBox(game: Data): void {
    if (!game) {
      return;
    }

    this.RequestPaymentService.claimPrizeBoxRequest(game?.game_id).subscribe({
      next: (request) => {
        const functions = getFunctions();
        functions.region = AuthService.FIREBASE_REGION;
        const api = httpsCallable(functions, "apisyncv2");

        from(api(request)).subscribe(
          async (response: Data) => {
            try {
              const claimPrizeBoxData = response?.data;
              if (claimPrizeBoxData.status === 'low_balance') {
                const translatedMsg = await this.authService.getConstantsMsg("1129");
                const defaultMsg = {
                  message: { line_1: "something went wrong!", line_2: "" },
                  style: { icon: "tick circle error.svg", type: "error" },
                };
                this.authService.showToastMessage(translatedMsg, defaultMsg);
              } else if (claimPrizeBoxData.status === 'success') {
                // Advance onboarding award/celebration (first_prize_box_opened),
                // then re-evaluate after the prize dialog closes.
                const prizeDialogRef = this.matDialog.open(PrizeBoxComponent, {
                  panelClass: "prizeBox-dialog-size",
                  backdropClass: "prizeBox-back-drop-color",
                  disableClose: true,
                  data: { prizeBoxData: game, claimPrizeData: claimPrizeBoxData },
                });
                this.gamesOnboardingService
                  .onFirstPrizeBoxOpened()
                  .pipe(take(1))
                  .subscribe({
                    error: (err) =>
                      console.error(
                        '[games-repository] onFirstPrizeBoxOpened failed',
                        err
                      ),
                  });
                prizeDialogRef.afterClosed().subscribe(() => {
                  void this.gamesOnboardingService.run().catch((err) => {
                    console.error(
                      '[games-repository] GamesOnboardingService.run after prize box failed',
                      err
                    );
                  });
                });
              }
            } catch (error) {
              return error;
            }
          },
          (error) => {
            return error;
          }
        );
      },
      error: (err) => {
        return err;
      },
    });
  }



  // This method is used to get the top prize values and user prize box affordability 
  prizeBoxValues(star_token: number, prizeBoxData: Data) {
    if (prizeBoxData) {
      const topPrizeBoxValues = prizeBoxData.map(data => data?.game_price).sort((a, b) => b - a);
      // Filter the prize box values that are less than or equal to the token value
      const filteredPrizeValues = topPrizeBoxValues.filter(value => value <= star_token);
      // Initialize with the first value in the filteredPrizeValues array
      let prizeBoxZero = filteredPrizeValues[0];
      // Initialize with the absolute difference between the first value and the star token
      let prizeBoxMinDifference = Math.abs(filteredPrizeValues[0] - star_token);
      // Iterate through the filteredPrizeValues array to find the closest value to the star token
      filteredPrizeValues.forEach(value => {
        const valueDifference = Math.abs(value - star_token);
        if (valueDifference < prizeBoxMinDifference) {
          prizeBoxZero = value;
          prizeBoxMinDifference = valueDifference;
        }
      });
      switch (topPrizeBoxValues.indexOf(prizeBoxZero)) {
        case 0:
          this.prizeBoxToast(1141);
          break;
        case 1:
          this.prizeBoxToast(1140);
          break;
        case 2:
          this.prizeBoxToast(1139);
          break;
        case 3:
          this.prizeBoxToast(1138);
          break;
      }
    }
  }

  // This method is use to display Prize Box Toast messages 
  prizeBoxToast(toastNumber?: number) {
    if (toastNumber) {
      try {
        this.authService
          .getConstantsMsg(toastNumber)
          .then((translatedMsg) => {
            const defaultMsg = {
              message: {
                line_1: "Something went wrong",
                line_2: "",
                duration: 2000,
              },
              style: {
                icon: "tick circle error.svg",
                type: CommonVariable.error,
              },
            };
            this.authService.showToastMessage(translatedMsg, defaultMsg);
          }).catch((error) => {
            return error;
          });
      } catch (error) {
        return error;
      }
    }
  }


}
